fx.service(class GoogleService extends fx.Service {
	constructor(model) {
		super();

		this.model = model.creationModel;

		this.data = this.model.google;

		// the photos of this import, staged for the screen and added to the album
		this._receivedPhotos = new fx.ModelArray();
		this._importing = false;

		this._initExtractor();

		this._client;
		this._pickerUri;
		this._sessionId;
		this._hasSelectedPhotos = false;
		this._accessToken = null;
		this._loadedPhotosAtOnce = [];

		this._NUM_ELEMENTS = fx.browser.isMobile ? 25 : 50;

		this._polls = 0;
		this.MAX_POLLS = 240;   // 2500 ms apart : ten minutes

		this.CLIENT_ID = '312072992255-qurkfdksm30hueojvl0j9m38bcm1dsmk.apps.googleusercontent.com';
		this.SCOPES = 'https://www.googleapis.com/auth/photospicker.mediaitems.readonly';
	}

	get processing() {
		return this._googleExtractor && this._googleExtractor.processing;
	}

	get total() {
		return this._googleExtractor ? this._googleExtractor.total : 0;
	}

	get position() {
		return this._googleExtractor ? this._googleExtractor.position : 0;
	}

	/****** Import source (see shared.BaseMain) ******/

	// the collection the import screen binds to
	get receivedPhotos() {
		return this._receivedPhotos;
	}

	// still fetching or extracting
	get pending() {
		return this._importing && this._receiving;
	}

	get _receiving() {
		return fx.service("GoogleConvertService").converting || this.processing;
	}

	// fired by the Main on validation ; every product already routes it
	get importedEvent() {
		return "google-photos-added";
	}

	// wording of the "not finished yet" confirmation
	get confirmEarly() {
		return {
			title : fx.text("Récupération pas encore terminée"),
			text : fx.text("La récupération des photos depuis Google n'est pas terminée. Souhaitez-vous importer seulement les $ photos affichées ?", this.receivedPhotos.length)
		};
	}

	// a fresh import : forget the previous one's photos
	startImport() {
		if (this._importing)
			return;

		this._importing = true;
		this._receivedPhotos.removeAll(true);
	}

	// stop fetching and drop what is not extracted ; receivedPhotos stays, for the Main to keep or delete
	stop() {
		this._importing = false;   // also what stops the media-list fetch below, which cannot be aborted

		this.closePicker();
		fx.service("GoogleConvertService").cancel();
		this._googleExtractor.clear();
	}

	// the single entry of both import paths : the web picker and the native one
	fetch(photos) {
		this.startImport();

		// what the import screen measures its progress bar against
		this.data.event("google-import-total", photos ? photos.length : 0);

		fx.service("GoogleConvertService").convert(photos);
	}

	get accessToken() {
		return this._accessToken;
	}

	set accessToken(val) {
		this._accessToken = val;
	}

	get token() {
		return this._accessToken;
	}

	/****** Google SDK ******/

	// STEP 1 => init google SDK

	// loads the Google SDK once ; `then` runs as soon as the client is ready
	initSDK(then) {
		if (this._client) {
			this._initTokenClient();

			if (then) then();
			return;
		}

		this._sdkThen = then || this._sdkThen;

		if (this._sdkLoading)
			return;

		this._sdkLoading = true;

		this.xhr = $.getScript('https://accounts.google.com/gsi/client', () => {
			this.xhr = null;
			this._sdkLoading = false;
			this._initTokenClient();

			let done = this._sdkThen;
			this._sdkThen = null;

			if (done) done();
		});
	}

	// STEP 2 => init client

	_initTokenClient() {
		if (this.data.length)
			this.data.removeAll();

		// Pre-login configuration step. Keep users sessions informations in this._client .
		// !!! requestAccessToken() function must be called in order to log-in and generate user token.
		this._client = google.accounts.oauth2.initTokenClient({
			client_id: this.CLIENT_ID,
			scope: this.SCOPES,
			callback: this._loginCallback.bind(this),

			// "" is "do not ask if you do not have to". A customer who has already granted the scope gets
			// a token without any popup at all, which leaves the click's user activation unspent — so the
			// picker that follows a second later still opens. With the default the SDK always shows an
			// account chooser, spends the one window the click allows, and the picker is then refused.
			// Only the very first grant falls back to that. Google shows the popup by itself when it
			// cannot answer silently, so nothing is lost by asking.
			prompt: '',

			error_callback: (e) => {   // popup dismissed, or refused : nothing is coming
				fx.log.error(e);
				this._authFailed();
			},
		});
	}

	// STEP 3 => login callback.
	// Return the token after user login, then create session.

	_loginCallback(tokenResponse) {
		if (tokenResponse.error) {
			fx.log.error(tokenResponse.error);
			this._authFailed();
		}
		else {
			if (tokenResponse && tokenResponse.access_token) {
				this.setToken(tokenResponse.access_token);
			}
		}
	}

	setToken(token) {
		this._accessToken = token;
		this._createSession();
	}

	// STEP 5 => Create user session

	_createSession(then) {
		let url = 'https://photospicker.googleapis.com/v1/sessions';
		fetch(url, {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${this._accessToken}`,
				'Content-Type': 'application/json',
			}
		})
			.then(response => response.json())
			.then(data => {
				// a refused session (expired token, quota) answers with an error body and no pickerUri,
				// and opening that would show a picker that never finishes loading
				if (!data || !data.pickerUri) {
					fx.log.error(data);

					if (then)   // the background pre-fetch : nothing is on screen waiting for it
						return;

					this._authFailed();
					return;
				}

				if (then)
					then(data);
				else
					this._startPolling(data);
			})
			.catch(error => {
				fx.log.error(error);

				if (!then)
					this._authFailed();
			});
	}

	/**
	 * A picker session is single use : once its photos are picked, re-opening its uri only ever shows a
	 * blank picker. The next one is fetched in the background so the following import finds a uri ready
	 * and skips the round trip through Google sign-in.
	 */
	_prepareNextSession() {
		this._createSession(session => { this._nextSession = session; });
	}

	get _sessionReady() {
		let session = this._nextSession;

		return !!(session && session.pickerUri && (!session.expireTime || new Date(session.expireTime) > new Date()));
	}

	// STEP 6 => Start polling to regulary check if user has selected photos

	_startPolling(data) {
		this._hasSelectedPhotos = false;
		this._sessionId = data.id;
		this._pickerUri = data.pickerUri;

		// straight to the picker rather than to a screen asking for one more click. Coming from the click
		// itself (a session was ready) the window always opens ; coming out of Google sign-in it is the
		// browser's call, and a refusal falls back to the screen below, which carries a real link.
		this.openPicker();

		// the picker is up, so the receiving screen takes over right away : the sign-in panel has nothing
		// left to offer while the customer is choosing photos in the Google window. If the browser refused
		// the window, that panel is still the way in — it carries the link.
		this.data.event(this._pickerOpened ? "google-picker-opened" : "import-google");
	}

	// the picker is already up, so the screen behind it has nothing to offer but a word about what to do
	get pickerOpen() {
		return !!this._pickerOpened;
	}

    // Allows to check if user selected pictures
    // need to be checked every X seconds
	pollSession() {
		if (++this._polls > this.MAX_POLLS) {   // left the picker without picking : stop asking
			this.closePicker();
			return;
		}

		if (this._sessionId) {
			let url = 'https://photospicker.googleapis.com/v1/sessions/' + this._sessionId;
			fetch(url, {
				method: 'GET',
				headers: {
					Authorization: `Bearer ${this._accessToken}`,
					'Content-Type': 'application/json',
				}
			}).then(response => response.json())
				.then(data => this._getPhotos(data))
				.catch(error => fx.log.error(error));
		}
		else {
			this.closePicker();
		}
	}

	// STEP 7 => If user has selected some photos, we fetch them

	_getPhotos(data) {
		if (data.mediaItemsSet && !this._hasSelectedPhotos) {
			let sessionId = this._sessionId;

			this._hasSelectedPhotos = true;
			this.closePicker();

			// spent : re-opening this uri would only ever show a blank picker
			this._sessionId = null;
			this._pickerUri = null;

			// the screen opens empty and fills up as the batches land
			this.startImport();

			this.data.event("google-selected-photos");

			this._allSelectedPhotos = [];

			this._fetchMediaPage(sessionId);

			if (!fx.get("flutter"))
				this._prepareNextSession();
		}
	}

	_fetchMediaPage(sessionId, pageToken) {
		let url = 'https://photospicker.googleapis.com/v1/mediaItems?session_id=' + sessionId;
		// if more than 50 pictures => google returns a pagination token, we add it to the url
		if (pageToken)
			url += '&pageToken=' + pageToken;
		fetch(url, {
			method: 'GET',
			headers: {
				Authorization: `Bearer ${this._accessToken}`,
				'Content-Type': 'application/json',
			}
		}).then(response => response.json())
			.then(data => {
				if (!this._importing)   // the customer left the import screen meanwhile
					return;

				if (data.mediaItems) {
					for (let i = 0; i < data.mediaItems.length; i++)
						this._allSelectedPhotos.push(this._convertPhoto(data.mediaItems[i]));
				}
				// if more than 50 pictures , we fetch the next page
				if (data.nextPageToken)
					this._fetchMediaPage(sessionId, data.nextPageToken);
				else {
					this.data.getServerPhotos(this._allSelectedPhotos);
					this._allSelectedPhotos = [];
				}

			})
			.catch(error => fx.log.error(error));
	}

	/****** USER ACTIONS ******/

	/**
	 * One button : signing in, creating the session and opening the picker all hang off this one click.
	 *
	 * With a session already in hand the picker opens from the click itself and nothing else is needed.
	 * Without one, the Google SDK spends the click's user activation on its own sign-in popup, and a
	 * browser grants only one window per click — so the picker that follows is at the browser's mercy,
	 * and a refusal falls back to the screen carrying the link.
	 *
	 * The app signs in natively : Google refuses OAuth in an embedded webview.
	 */
	login() {
		if (fx.get("flutter")) {
			fx.get("flutter").GoogleAuth();
			return;
		}

		// a session is already waiting, prepared as soon as the previous import's photos were read : the
		// picker opens from this very click, without a round trip through Google
		if (this._sessionReady) {
			let session = this._nextSession;
			this._nextSession = null;

			this._startPolling(session);
			return;
		}

		// Nothing is opened here. A click buys ONE window, and the Google SDK needs it for its own
		// sign-in popup : taking it first left Google with none and the sign-in never appeared. Which is
		// also why the picker cannot be opened on the way back — that window would be the second one.

		if (this._client)
			this._client.requestAccessToken();	// open login popup and request token after user logged
		else
			this.initSDK(() => {
				if (this._client)
					this._client.requestAccessToken();
				else
					this._authFailed();
			});
	}

	/**
	 * The picker uri, for the fallback link : only reached when the browser refused the window this opened
	 * by itself. It has to be a real link — on mobile fx maps click to Hammer's tap, which is emitted from
	 * a timer, so a window.open called there sits outside the user gesture and gets blocked too.
	 */
	get pickerUrl() {
		return this._pickerUri || "";
	}

	// the customer is off to the picker : watch the session for their selection
	watchPicker() {
		this._polls = 0;
		this.repeat(this.pollSession, 2500);	// 2500 ms polling
	}

	/**
	 * No features string, and "_blank" : that is what Cheerz does, and it is the one difference left
	 * between their call and ours. A features string is what turns window.open into a popup ; without one
	 * the browser opens a plain tab, and the two are not weighed the same when the call comes from outside
	 * a click — which is where ours lands, after Google has spent the click on its own sign-in.
	 */
	_openWindow(url) {
		return window.open(url, "_blank");
	}

	/**
	 * Reuses the window we already hold when there is one — navigating a window of ours needs no user
	 * activation — and asks for a new one otherwise.
	 */
	openPickerWindow() {
		if (!this._pickerUri)
			return null;

		if (this._activeWindow && !this._activeWindow.closed) {
			this._activeWindow.location = this._pickerUri;
			return this._activeWindow;
		}

		this._activeWindow = this._openWindow(this._pickerUri);

		return this._activeWindow;
	}

	/**
	 * Hangs the sign-in off a component's own click, natively. Not an fx click handler : on a phone fx
	 * maps click to AlloyFinger's tap, which is dispatched from a setTimeout (vendors/alloyfinger.js:245),
	 * so the window openPickerWindow takes would sit outside the user gesture and be refused. That matters
	 * on the path where a session is already in hand : there the picker is opened from the click itself.
	 *
	 * `handler` is the component's own click handler, so a subclass overriding it still gets its way —
	 * the app, for one, signs in through Flutter and closes its panel. It defaults to signing in.
	 */
	bindSignIn(component, handler) {
		if (!component || !component.node)
			return;

		component.node.addEventListener("click", handler || (() => this.login()));
	}

	// the sign-in went nowhere : let the held window go rather than leave it spinning
	_authFailed() {
		this.closePicker();
		this.data.event("google-photos-error");
	}

	openPicker() {
		// the previous session was spent : get a new one rather than open a picker that cannot load
		if (!this._pickerUri) {
			this.login();
			return;
		}

		// the app opens the picker natively (no Google page in a webview) ; the session is the way back
		if (fx.get("flutter")) {
			fx.get("flutter").GoogleWebview(this._pickerUri);
			this._pickerOpened = true;
		}
		else
			this._pickerOpened = !!this.openPickerWindow();

		this.watchPicker();
	}

	closePicker() {
		this.repeat(this.pollSession, null);

		this._pickerOpened = false;

		// in the app the picker lives in a browser view we hold no handle on : the native side is the
		// only one that can close it
		if (fx.get("flutter"))
			fx.get("flutter").GoogleWebviewClose();
		else
		if (this._activeWindow)
			this._activeWindow.close();
	}

	/****** FUNCTIONS ******/

	_initExtractor() {
		if (!this._googleExtractor) {
			this._googleExtractor = new shared.extractors.GoogleExtractor({ data: this.model });
			this._googleExtractor.received = this._receivedPhotos;
			this.on("photo-extracted", this._onGooglePhotoExtracted, this._googleExtractor);
			this.on("photo-extracted-finished", this._onGooglePhotoExtractedFinished, this._googleExtractor);

			let convert = fx.service("GoogleConvertService");
			this.on("google-converted-batch", this._onConvertedBatch, convert);
			this.on("google-convert-finished", this._onConvertFinished, convert);
		}
	}

	// extract and upload each batch as it lands, without waiting for the rest
	_onConvertedBatch(e) {
		this.upload(e.detail);
	}

	// nothing left to fetch : the extraction normally closes the import, but when there is nothing to
	// extract (an empty answer, or a failed fetch) this is the end
	_onConvertFinished(e) {
		if (e.detail.error)
			this.data.event("google-selected-photos-error");

		this._endImport();
	}

	_onGooglePhotoExtracted(e) {
		// send pictures to MediaUpoloader so they can be stored on server
		this.trigger("photo-extracted", e.detail);
		fx.get("flutter") ? fx.service("GoogleUploader").upload(e.detail) : fx.service("MediasUploader").upload(e.detail)
	}

	// the extractor drains after every batch : forward once, when the server has nothing left either
	_onGooglePhotoExtractedFinished(e) {
		if (fx.service("GoogleConvertService").converting)
			return;

		this.trigger("photo-extracted-finished", e.detail);

		this._endImport();
	}

	// the import screen leaves its receiving state on this one
	_endImport() {
		if (!this._importing || this._receiving)
			return;

		this._importing = false;

		this.data.event("google-import-finished");
	}

	upload(selectedPhotos) {
		this._googleExtractor.process(selectedPhotos);
	}

	_convertPhoto(googlePhoto) {
		let photo = {
            createTime : googlePhoto.createTime,
			baseUrl: googlePhoto.mediaFile.baseUrl,
			id: googlePhoto.id,
			mediaMetadata: googlePhoto.mediaFile.mediaFileMetadata,
			productUrl: googlePhoto.mediaFile.productUrl
		};

		photo.thumb = `${photo.baseUrl}=w256-h256-c`;

		photo.fullSize_url = `${photo.baseUrl}=w${photo.mediaMetadata.width}-h${photo.mediaMetadata.height}`;
		photo._m = "GooglePhotoModel";

		return photo;
	}
});